// CommentPopup.tsx - Updated with loading states
import React, { useState, useRef, useEffect } from 'react';
import { Close } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';

interface CommentPopupProps {
  onAddComment: (text: string) => void;
  onClose: () => void;
  position: { top: number; left: number };
  selectedText: string;
  isVisible: boolean;
  isLoading?: boolean; // Add loading prop
}

const CommentPopup: React.FC<CommentPopupProps> = ({
  onAddComment,
  onClose,
  position,
  selectedText,
  isVisible,
  isLoading = false, // Default to false
}) => {
  const [comment, setComment] = useState('');
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // Adjust position to keep popup within viewport
  useEffect(() => {
    if (!isVisible || !popupRef.current) return;

    const popup = popupRef.current;
    const rect = popup.getBoundingClientRect();
    const isMobile = window.innerWidth < 768;

    let newTop = position.top;
    let newLeft = position.left;

    if (isMobile) {
      // On mobile, center the popup horizontally with padding
      const popupWidth = 280;
      const viewportWidth = window.innerWidth;
      newLeft = Math.max(16, Math.min(position.left, viewportWidth - popupWidth - 16));
    } else {
      // On desktop, ensure popup doesn't go off-screen
      if (rect.right > window.innerWidth) {
        newLeft = window.innerWidth - rect.width - 16;
      }
      if (rect.left < 0) {
        newLeft = 16;
      }
    }

    // Adjust vertical position if needed
    if (rect.bottom > window.innerHeight) {
      newTop = window.innerHeight - rect.height - 16;
    }
    if (newTop < 0) {
      newTop = 16;
    }

    setAdjustedPosition({ top: newTop, left: newLeft });
  }, [isVisible, position]);

  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName !== 'TEXTAREA' && target.tagName !== 'BUTTON') {
      e.preventDefault();
    }
    e.stopPropagation();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (comment.trim() && !isLoading) {
      onAddComment(comment);
      setComment('');
    }
  };

  const handleInputClick = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    e.stopPropagation();
    if (!isLoading) {
      inputRef.current?.focus();
    }
  };

  if (!isVisible) return null;

  return (
    <div
      ref={popupRef}
      className="comment-popup"
      style={{
        position: 'fixed',
        top: adjustedPosition.top,
        left: adjustedPosition.left,
        width: 'min(280px, calc(100vw - 32px))',
        maxWidth: '280px',
        backgroundColor: 'white',
        border: '1px solid #dee2e6',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 1001,
        padding: 12,
        boxSizing: 'border-box',
      }}
      onMouseDown={handleMouseDown}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 8,
          width: '100%',
        }}
      >
        <div style={{ flex: 1, overflow: 'hidden', paddingRight: 8 }}>
          <div
            style={{
              fontSize: 11,
              color: '#6c757d',
              marginBottom: 4,
              fontStyle: 'italic',
              fontFamily: 'Inter, sans-serif',
              wordBreak: 'break-word',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            "{selectedText.length > 50 ? selectedText.substring(0, 50) + '...' : selectedText}"
          </div>
        </div>
        <button
          onClick={onClose}
          disabled={isLoading} // Disable close button when loading
          style={{
            background: 'none',
            border: 'none',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            padding: 2,
            color: '#6c757d',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Close style={{ fontSize: 14 }} />
        </button>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          gap: 8,
          flexDirection: 'column',
          alignItems: 'flex-end',
          width: '100%',
        }}
      >
        <textarea
          ref={inputRef}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add a comment here"
          onClick={handleInputClick}
          disabled={isLoading} // Disable input when loading
          style={{
            width: '100%',
            maxWidth: '100%',
            minHeight: 60,
            padding: '6px 8px',
            border: '1px solid #1B54F8',
            borderRadius: 4,
            fontSize: 12,
            outline: 'none',
            fontFamily: 'Inter, sans-serif',
            resize: 'vertical',
            boxSizing: 'border-box',
            backgroundColor: isLoading ? '#f8f9fa' : 'white',
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !isLoading) {
              onClose();
            }
          }}
        />
        <button
          type="submit"
          disabled={!comment.trim() || isLoading}
          style={{
            padding: '8px 16px',
            backgroundColor: comment.trim() && !isLoading ? '#007bff' : '#e9ecef',
            color: comment.trim() && !isLoading ? 'white' : '#6c757d',
            border: 'none',
            borderRadius: 4,
            cursor: comment.trim() && !isLoading ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '51px',
            fontSize: 12,
            fontFamily: 'Inter, sans-serif',
            transition: 'background-color 0.2s ease',
          }}
        >
          {isLoading ? <CircularProgress size={14} style={{ color: '#6c757d' }} /> : 'Add'}
        </button>
      </form>
    </div>
  );
};

export default CommentPopup;
